// Shown immediately after a successful login when the user's `first_login`
// flag is still 1.  They cannot reach /management until they replace the
// temporary password their super_admin issued with one of their own
// choosing — and that one MUST satisfy the strength rules.
//
// Once /changepassword succeeds we refresh the cached user (first_login → 0)
// and forward to /management.

import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { changePassword as changePasswordUrl } from "../BaseURL";
import { passwordIssue, passwordScore, STRENGTH_LABELS } from "../utils/password";

export default function ForceChangePassword() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Anyone landing here without a session shouldn't see the form.
  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const issue = passwordIssue(next);
  const score = passwordScore(next);
  const tooShortToScore = next.length === 0;
  const meterColor = ["#e74c3c", "#e67e22", "#c8a05a", "#4a7c59", "#2d6a4f"][score];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!current) { setError("Enter your temporary password"); return; }
    if (issue)    { setError(issue); return; }
    if (next !== confirm) { setError("Passwords do not match"); return; }

    setBusy(true);
    try {
      const res = await axios.post(changePasswordUrl, {
        current_password: current,
        new_password: next,
      });
      if (!res.data?.success) {
        setError(res.data?.message || "Could not change password");
        return;
      }
      // Mark the cached user as no longer first-login so the router stops
      // bouncing us back here.
      const updated = { ...user, first_login: false };
      localStorage.setItem("user", JSON.stringify(updated));
      navigate("/management", { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Jost:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .fcp-root { min-height: 100vh; background: #f5ede2; display: grid; place-items: center; padding: 32px 16px; font-family: 'Jost', sans-serif; }
        .fcp-card { width: min(460px, 100%); background: #fdf8f2; border-radius: 16px; overflow: hidden; border: 1px solid rgba(180,140,80,.18); box-shadow: 0 18px 60px rgba(40,20,5,.18); }
        .fcp-head { background: linear-gradient(135deg, #2c1f14 0%, #3d2a1a 100%); color: #f0e4cf; padding: 32px 36px 26px; }
        .fcp-eyebrow { font-size: 10.5px; letter-spacing: .22em; text-transform: uppercase; color: #c8a05a; margin-bottom: 8px; font-weight: 500; }
        .fcp-title { font-family: 'Cormorant Garamond', serif; font-weight: 300; font-size: 28px; line-height: 1.15; }
        .fcp-title em { font-style: italic; color: #c8a05a; }
        .fcp-sub { font-size: 13px; font-weight: 300; color: rgba(240,228,207,.7); margin-top: 10px; line-height: 1.55; }
        .fcp-body { padding: 26px 36px 32px; }
        .fcp-group { margin-bottom: 16px; }
        .fcp-label { display: block; font-size: 10.5px; font-weight: 500; letter-spacing: .15em; text-transform: uppercase; color: #6b5a4e; margin-bottom: 7px; }
        .fcp-input-wrap { position: relative; }
        .fcp-input { width: 100%; padding: 12px 44px 12px 14px; border: 1.5px solid #e0d0c0; border-radius: 8px; background: #fff; font-family: 'Jost', sans-serif; font-size: 14px; color: #2c1f14; outline: none; transition: border-color .2s, box-shadow .2s; }
        .fcp-input:focus { border-color: #c8a05a; box-shadow: 0 0 0 3px rgba(200,160,90,.1); }
        .fcp-show { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #8a7060; font-size: 12px; font-weight: 500; letter-spacing: .1em; }
        .fcp-meter { display: flex; gap: 5px; margin-top: 8px; }
        .fcp-meter-bar { flex: 1; height: 4px; border-radius: 3px; background: rgba(180,140,80,.15); transition: background .25s; }
        .fcp-meter-label { font-size: 11.5px; color: #8a7060; margin-top: 6px; font-weight: 300; }
        .fcp-rules { font-size: 12px; color: #8a7060; margin-top: 6px; line-height: 1.55; font-weight: 300; }
        .fcp-err { background: #fef0ee; border: 1px solid #f5c5bc; color: #a93226; padding: 11px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 14px; }
        .fcp-submit { width: 100%; padding: 13px; background: linear-gradient(135deg, #2c1f14, #3d2a1a); color: #f0e4cf; border: none; border-radius: 8px; font-size: 12px; letter-spacing: .18em; text-transform: uppercase; font-weight: 500; cursor: pointer; transition: transform .15s, filter .2s; margin-top: 6px; }
        .fcp-submit:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.06); }
        .fcp-submit:disabled { opacity: .65; cursor: not-allowed; }
      `}</style>
      <div className="fcp-root">
        <div className="fcp-card">
          <div className="fcp-head">
            <div className="fcp-eyebrow">First-time sign-in</div>
            <div className="fcp-title">Choose your <em>password</em></div>
            <div className="fcp-sub">
              Welcome{user?.full_name ? `, ${user.full_name}` : ""}. You're using
              a temporary password issued by an administrator. Please set your
              own before continuing.
            </div>
          </div>

          <form className="fcp-body" onSubmit={submit}>
            {error && <div className="fcp-err">⚠️ {error}</div>}

            <div className="fcp-group">
              <label className="fcp-label">Current (temporary) password</label>
              <div className="fcp-input-wrap">
                <input
                  className="fcp-input"
                  type={show ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  required
                />
              </div>
            </div>

            <div className="fcp-group">
              <label className="fcp-label">New password</label>
              <div className="fcp-input-wrap">
                <input
                  className="fcp-input"
                  type={show ? "text" : "password"}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button type="button" className="fcp-show" onClick={() => setShow((s) => !s)}>
                  {show ? "HIDE" : "SHOW"}
                </button>
              </div>
              <div className="fcp-meter">
                {[0,1,2,3].map(i => (
                  <div
                    key={i}
                    className="fcp-meter-bar"
                    style={{ background: !tooShortToScore && i < score ? meterColor : "rgba(180,140,80,.15)" }}
                  />
                ))}
              </div>
              <div className="fcp-meter-label">
                {tooShortToScore ? "Strength: —" : `Strength: ${STRENGTH_LABELS[score]}`}
              </div>
              <div className="fcp-rules">
                Minimum 6 characters. Mix at least two of: lowercase letters, uppercase letters, numbers, symbols.
              </div>
            </div>

            <div className="fcp-group">
              <label className="fcp-label">Confirm new password</label>
              <input
                className="fcp-input"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <button type="submit" className="fcp-submit" disabled={busy}>
              {busy ? "Saving…" : "Save & Continue"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
